import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { Scan, Shield, Globe, Code } from "lucide-react";

interface ScanDialogProps {
  children: React.ReactNode;
}

export function ScanDialog({ children }: ScanDialogProps) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [scanType, setScanType] = useState("full");
  const [requiresAuth, setRequiresAuth] = useState(false);
  const [authUsername, setAuthUsername] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [authLoginUrl, setAuthLoginUrl] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const startScanMutation = useMutation({
    mutationFn: async (data: { url: string; type: string; auth?: { username: string; password: string; loginUrl?: string } }) => {
      const response = await fetch("/api/scans", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Include session cookies
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to start scan: ${response.statusText}`);
      }
      
      return response.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Scan started",
        description: `Security scan initiated for ${url}. You'll receive real-time updates.`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/scans"] });
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard/stats"] });
      setOpen(false);
      setUrl("");
      setScanType("full");
    },
    onError: (error: any) => {
      toast({
        title: "Failed to start scan",
        description: error.message || "An error occurred while starting the scan",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Debug logging for laptop deployment
    console.log('Form submit triggered:', { url, scanType });
    
    // Enhanced URL validation for cross-platform compatibility
    const trimmedUrl = url?.trim();
    if (!trimmedUrl || trimmedUrl.length === 0) {
      console.log('URL validation failed: empty URL');
      toast({
        title: "URL required",
        description: "Please enter a URL to scan",
        variant: "destructive",
      });
      return;
    }

    // More robust URL validation
    let validUrl = trimmedUrl;
    try {
      // Add protocol if missing
      if (!validUrl.match(/^https?:\/\//i)) {
        validUrl = `https://${validUrl}`;
        console.log('Added https protocol:', validUrl);
      }
      
      // Validate URL
      const urlObj = new URL(validUrl);
      console.log('URL validation passed:', urlObj.href);
    } catch (error) {
      console.log('URL validation failed:', error);
      toast({
        title: "Invalid URL",
        description: "Please enter a valid URL (e.g., google.com or https://google.com)",
        variant: "destructive",
      });
      return;
    }

    // Prepare scan data with optional authentication
    const scanData: { url: string; type: string; auth?: { username: string; password: string; loginUrl?: string } } = {
      url: validUrl,
      type: scanType
    };
    
    // Add authentication if provided
    if (requiresAuth && authUsername && authPassword) {
      scanData.auth = {
        username: authUsername,
        password: authPassword,
        loginUrl: authLoginUrl || validUrl // Use provided login URL or default to scan URL
      };
    }
    
    // Enhanced form submission with debugging
    console.log('Submitting scan request:', scanData);
    startScanMutation.mutate(scanData);
  };

  const scanTypeOptions = [
    {
      value: "full",
      label: "Full Security Scan",
      description: "Complete analysis including headers, JavaScript, and vulnerabilities",
      icon: <Shield className="h-4 w-4" />,
    },
    {
      value: "javascript",
      label: "JavaScript Integrity",
      description: "Monitor script changes and detect suspicious patterns",
      icon: <Code className="h-4 w-4" />,
    },
    {
      value: "quick",
      label: "Quick Scan",
      description: "Fast security header and basic vulnerability check",
      icon: <Globe className="h-4 w-4" />,
    },
  ];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scan className="h-5 w-5" />
            Start Security Scan
          </DialogTitle>
          <DialogDescription>
            Launch a comprehensive security scan to analyze your website for vulnerabilities,
            compliance issues, and JavaScript integrity.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="url">Website URL</Label>
              <Input
                id="url"
                placeholder="https://example.com"
                value={url}
                onChange={(e) => {
                  const newUrl = e.target.value;
                  console.log('URL input changed:', newUrl);
                  setUrl(newUrl);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
                data-testid="input-scan-url"
                autoComplete="off"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="scanType">Scan Type</Label>
              <Select value={scanType} onValueChange={setScanType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select scan type" />
                </SelectTrigger>
                <SelectContent>
                  {scanTypeOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <div className="flex items-start gap-2">
                        {option.icon}
                        <div>
                          <div className="font-medium">{option.label}</div>
                          <div className="text-sm text-muted-foreground">
                            {option.description}
                          </div>
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Authentication Section */}
            <div className="space-y-3 pt-2 border-t">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="requiresAuth"
                  checked={requiresAuth}
                  onChange={(e) => setRequiresAuth(e.target.checked)}
                  className="rounded border-slate-300"
                />
                <Label htmlFor="requiresAuth" className="font-medium cursor-pointer">
                  Requires Authentication
                </Label>
              </div>
              
              {requiresAuth && (
                <div className="space-y-3 pl-6 border-l-2 border-purple-200 dark:border-purple-800">
                  <div className="space-y-2">
                    <Label htmlFor="authLoginUrl" className="text-sm">
                      Login URL (optional, defaults to scan URL)
                    </Label>
                    <Input
                      id="authLoginUrl"
                      type="url"
                      placeholder="https://example.com/login"
                      value={authLoginUrl}
                      onChange={(e) => setAuthLoginUrl(e.target.value)}
                      className="text-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="authUsername" className="text-sm">
                      Username
                    </Label>
                    <Input
                      id="authUsername"
                      type="text"
                      placeholder="Enter username"
                      value={authUsername}
                      onChange={(e) => setAuthUsername(e.target.value)}
                      className="text-sm"
                      required={requiresAuth}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="authPassword" className="text-sm">
                      Password
                    </Label>
                    <Input
                      id="authPassword"
                      type="password"
                      placeholder="Enter password"
                      value={authPassword}
                      onChange={(e) => setAuthPassword(e.target.value)}
                      className="text-sm"
                      required={requiresAuth}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    The scanner will log in before scanning to access authenticated pages.
                  </p>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={startScanMutation.isPending}>
              {startScanMutation.isPending ? "Starting..." : "Start Scan"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}